import * as React from 'react';
import { AppLink, navigate } from 'mydish-ui-util';
import { useHistory } from 'react-router-dom';
import NavButton from './NavButton';
import '../css/global-nav.sass';
import DrawerComponent, {
  ChildPage,
  EvergreenLink,
  SectionPage
} from './DrawerComponent';
import SearchInput from './SearchInput';
import searchIcon from '../css/assets/img/search-smoke.svg';
import CampfireButton from './CampfireButton';
import Anchor from './Anchor';

export interface SelectedNavigation {
  topNav: string;
  drawerNav: string;
}

export interface Navigation {
  id?: string;
  url?: string;
  name: string;
  pageName: string;
  pageURL: string;
  sectionPages?: SectionPage[];
  childPages?: ChildPage[];
}

interface TopNavProps {
  theme?: 'night' | 'day';
  appName?: string;
  searchUrl?: string;
  searchLabel?: string;
  signOutUrl?: string;
  signOutLabel?: string;
  signInUrl?: string;
  signInLabel?: string;
  isLoggedIn?: boolean;
  pageContentId?: string;
  skipToContentText?: string;
  navigations: Navigation[];
  navEvergreenLinks: EvergreenLink[];
  selectedNavigation: SelectedNavigation;
  logo: string;
  onSignIn?: Function;
  onSignOut?: Function;
  displaySignInOutIcon?: boolean;
  collapsibleHeaders?: boolean;
  enablePrimaryLinkNavigation?: boolean;
}

interface TopNavState {
  selectedNavName: string;
  navSelected: boolean;
  displaySearchInput: boolean;
  searchText?: string;
  toggleMenuOpen: boolean;
  defaultNavName: string;
  selectedNavId: string;
}

export default function TopNavigation(props: TopNavProps) {
  const {
    pageContentId = 'pageContent',
    skipToContentText = 'Skip to content',
    theme = 'night',
    signOutLabel = 'Sign Out',
    signInLabel = 'Sign In',
    appName = 'MyDISH',
    searchLabel = 'Search'
  } = props;

  const history = useHistory();

  const signInOutUrl = props.isLoggedIn ? props.signOutUrl : props.signInUrl;
  const signInOutLabel = props.isLoggedIn ? signOutLabel : signInLabel;
  const displaySignInOut = !!signInOutUrl || props.onSignOut || props.onSignIn;

  const [state, setState] = React.useState<TopNavState>({
    selectedNavName:
      props.selectedNavigation && props.selectedNavigation.topNav,
    navSelected: false,
    displaySearchInput: false,
    toggleMenuOpen: false,
    defaultNavName: '',
    selectedNavId: ''
  });

  React.useEffect(() => {
    setState({
      ...state,
      selectedNavName:
        props.selectedNavigation && props.selectedNavigation.topNav,
      navSelected: false
    });
  }, [props]);

  if (state.toggleMenuOpen || state.navSelected) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.removeAttribute('style');
  }

  let keyIndex = 0;
  const navOptions = (view: string) => {
    return props.navigations.map((navigation: Navigation, index: number) => {
      const navName = navigation.name || navigation.pageName;
      keyIndex += 1;
      return (
        <div
          key={`${navName}${keyIndex}`}
          className={`navOptions${state.displaySearchInput ? ' hidden' : ''}`}
          id={`${view}-nav-option-${index}`}
        >
          <NavButton
            id={`${view}-nav-link-${index}`}
            className={`nav-link ${
              state.toggleMenuOpen && !props.enablePrimaryLinkNavigation
                ? 'arrow-right'
                : ''
            }`}
            label={navName}
            active={
              state.selectedNavName === navName ||
              (!state.selectedNavName && state.toggleMenuOpen && index === 0)
            }
            ariaExpanded={
              state.navSelected && state.selectedNavName === navName
            }
            onClick={() => {
              if (props.enablePrimaryLinkNavigation) {
                const href = navigation.url || navigation.pageURL;
                navigate(href, history);
              } else {
                setState({
                  ...state,
                  selectedNavName: navName,
                  navSelected: true,
                  selectedNavId: `${view}-nav-link-${index}`
                });
              }
            }}
            onKeyDown={(e: any) => {
              if (e.keyCode === 13 || e.keyCode === 32) {
                setState({
                  ...state,
                  selectedNavName: navName,
                  navSelected: true,
                  selectedNavId: `${view}-nav-link-${index}`
                });
              }
            }}
          />
        </div>
      );
    });
  };

  const mobileNavigation = (
    <div className="mobileNav">
      <button
        type="button"
        className={`burger ${
          state.toggleMenuOpen || state.navSelected ? ' open' : ''
        }`}
        aria-label="Navigation menu"
        aria-expanded={state.toggleMenuOpen}
        onMouseDown={() => {
          const defaultNavName =
            props.navigations[0].name || props.navigations[0].pageName;
          setState({
            ...state,
            toggleMenuOpen: !state.toggleMenuOpen,
            defaultNavName
          });
        }}
      >
        <span className="top-bun" />
        <span className="patty-1" />
        <span className="patty-2" />
        <span className="bottom-bun" />
      </button>
    </div>
  );
  const mobileAccountIcon = displaySignInOut && (
    <button
      type="button"
      className={`accountIcon 
      ${props.displaySignInOutIcon ? 'signInIcon' : ''}
    `}
      aria-label="Account Icon"
      onMouseDown={(e: any) => {
        e.preventDefault();
      }}
      onClick={() => {
        if (props.isLoggedIn && props.onSignOut) {
          props.onSignOut();
        } else if (!props.isLoggedIn && props.onSignIn) {
          props.onSignIn();
        } else if (signInOutUrl) {
          navigate(`${signInOutUrl}`, history);
        }
      }}
    >
      <div
        className={`accountIcon 
                    ${props.displaySignInOutIcon ? 'signInIcon' : ''}
                  `}
      />
    </button>
  );

  const navName = state.selectedNavName
    ? state.selectedNavName
    : state.defaultNavName;

  let selectedSectionPages: SectionPage[] = [];
  if (navName) {
    const selectedNavigation = props.navigations.filter(
      (nav: Navigation) => nav.name === navName || nav.pageName === navName
    );
    if (
      selectedNavigation &&
      selectedNavigation.length > 0 &&
      selectedNavigation[0]
    ) {
      if (
        selectedNavigation[0].sectionPages &&
        selectedNavigation[0].sectionPages.length > 0
      ) {
        selectedSectionPages = selectedNavigation[0].sectionPages;
      } else if (selectedNavigation[0].childPages) {
        selectedSectionPages = [
          { name: '', childPages: selectedNavigation[0].childPages }
        ];
      }
    }
  }

  return (
    <>
      <div
        className="skipNav"
        tabIndex={0}
        onMouseDown={(e) => {
          e.preventDefault();
          const anchorTag = e.currentTarget.firstElementChild;
          if (anchorTag instanceof window.HTMLElement) {
            anchorTag.click();
          }
        }}
        onKeyDown={(e) => {
          if (e.keyCode === 32 || e.keyCode === 13) {
            e.preventDefault();
            const anchorTag = e.currentTarget.firstElementChild;
            if (anchorTag instanceof window.HTMLElement) {
              anchorTag.click();
            }
          }
        }}
        role="button"
      >
        <Anchor
          arialabel="skipToContent"
          aria-label="skipToContent"
          id="skipToContent"
          href={`#${pageContentId}`}
          tabIndex={-1}
        >
          {skipToContentText}
        </Anchor>
      </div>
      <nav
        className={`${theme === 'night' ? 'night' : ''} ${
          state.toggleMenuOpen || state.navSelected ? 'menuOpen' : ''
        }`}
      >
        <div className="nav-left">
          {mobileNavigation}
          <AppLink href="/" className="nav-logo">
            <img
              alt={`${appName} homepage`}
              src={props.logo}
              title="Go to homepage"
            />
          </AppLink>
          {navOptions('menu')}
        </div>

        <div className="nav-right">
          <div className="nav-search">
            {props.searchUrl &&
              (state.displaySearchInput ? (
                <SearchInput
                  focus
                  value={state.searchText}
                  onSearch={(searchText: string) => {
                    navigate(
                      `${props.searchUrl?.replace('{searchText}', searchText)}`,
                      history
                    );
                  }}
                  onBlur={(e: any) => {
                    setState({
                      ...state,
                      searchText: e.target.value,
                      displaySearchInput: false
                    });
                  }}
                  name="globalSearch"
                  id="globalSearch"
                  label={searchLabel}
                  theme={theme}
                />
              ) : (
                <input
                  type="image"
                  alt="search"
                  src={searchIcon}
                  className="nav-search-icon"
                  onClick={() => {
                    setState({ ...state, displaySearchInput: true });
                  }}
                />
              ))}
          </div>
          {displaySignInOut && (
            <div className="nav-sign-in">
              {mobileAccountIcon}
              <span className="signOutBtn">
                {!props.displaySignInOutIcon && (
                  <CampfireButton
                    name="button"
                    id="button"
                    label={signInOutLabel}
                    color="marshmallow"
                    ghost
                    smol
                    theme={theme}
                    onClick={() => {
                      if (props.isLoggedIn && props.onSignOut) {
                        props.onSignOut();
                      } else if (!props.isLoggedIn && props.onSignIn) {
                        props.onSignIn();
                      } else if (signInOutUrl) {
                        navigate(`${signInOutUrl}`, history);
                      }
                    }}
                  />
                )}
              </span>
            </div>
          )}
        </div>
        <DrawerComponent
          sectionPages={selectedSectionPages}
          selectedDrawer={
            props.selectedNavigation && props.selectedNavigation.drawerNav
          }
          onBlur={() => {
            const selectedNavItem = document.getElementById(
              state.selectedNavId
            );
            if (selectedNavItem) {
              selectedNavItem.focus();
            }
            setState({ ...state, navSelected: false, toggleMenuOpen: false });
          }}
          navOptions={navOptions('drawer')}
          navEvergreenLinks={props.navEvergreenLinks}
          navSelected={state.navSelected}
          toggleMenuOpen={state.toggleMenuOpen}
          selectedNavName={state.selectedNavName}
          collapsibleHeaders={props.collapsibleHeaders}
          backToMenu={() => {
            setState({ ...state, navSelected: false });
          }}
          enablePrimaryLinkNavigation={props.enablePrimaryLinkNavigation}
        />
      </nav>
    </>
  );
}
